/**
 * Social: the chat ping, emotes and emoji, the party, townsfolk speaking, and the group dance —
 * its drum beat on each arrow, hits and misses, rounds, and the end.
 */
import { registerSfx } from '../registry';
import { chime, drum, gong, note, ROOT_D, strum, woodblock } from '../voices';

registerSfx('social.chat', { label: 'ข้อความแชท', cat: 'social', vary: 0, gap: 0.25 }, (k) => {
  k.tone({ f: 1318.5, dur: 0.12, vol: 0.06 });
  k.tone({ f: 1760, at: 0.07, dur: 0.18, vol: 0.05 });
});

registerSfx('social.emote', { label: 'ท่าทาง (อีโมต)', cat: 'social', vary: 0.05, gap: 0.3 }, (k) => {
  k.noise({ dur: 0.2, vol: 0.12, attack: 0.04, f: 1100, f1: 2200, q: 1 });
  k.pluck({ f: note(ROOT_D * 2, k.pick([0, 2, 4])), at: 0.05, dur: 0.6, vol: 0.08 });
});

registerSfx('social.emoji', { label: 'อีโมจิเด้ง', cat: 'social', vary: 0.08, gap: 0.2 }, (k) => {
  k.tone({ f: 500, f1: 1300, dur: 0.09, vol: 0.08 });
  k.tone({ f: 1500, at: 0.06, dur: 0.1, vol: 0.03 });
});

registerSfx('social.party', { label: 'ปาร์ตี้เปลี่ยน', cat: 'social', vary: 0, gap: 0.3 }, (k) => {
  strum(k, ROOT_D, [2, 4, 7], { gap: 0.06, vol: 0.09, dur: 0.9 });
});

registerSfx('social.bark', { label: 'ชาวบ้านพูด', cat: 'social', vary: 0.15, gap: 0.6, gain: 0.7 }, (k) => {
  // A murmur, not words: a voiced buzz through a moving vowel.
  const bp = k.filter('bandpass', 700, 4);
  bp.frequency.setValueAtTime(k.f(600), k.t);
  bp.frequency.linearRampToValueAtTime(k.f(1100), k.t + 0.12);
  bp.frequency.linearRampToValueAtTime(k.f(800), k.t + 0.25);
  k.tone({
    type: 'sawtooth',
    f: k.range(160, 240),
    f1: k.range(140, 200),
    dur: 0.28,
    vol: 0.06,
    attack: 0.03,
    out: bp,
  });
});

// ───────── group dance ─────────

registerSfx(
  'social.dance.start',
  { label: 'รำกลุ่ม: เริ่ม', cat: 'social', vary: 0, gap: 1, preview: 2.4 },
  (k) => {
    for (let i = 0; i < 8; i++) drum(k, { f: 140, at: i * 0.07, vol: 0.12 + i * 0.02, dur: 0.15 });
    gong(k, { f: 196, at: 0.6, dur: 1.6, vol: 0.08 });
  },
);

registerSfx(
  'social.dance.beat',
  { label: 'รำกลุ่ม: จังหวะกลอง', cat: 'social', vary: 0.01, gap: 0.08 },
  (k) => {
    drum(k, { f: 150, vol: 0.28, dur: 0.25 });
    woodblock(k, { f: 1200, vol: 0.05 });
  },
);

registerSfx('social.dance.hit', { label: 'รำกลุ่ม: ตรงจังหวะ', cat: 'social', vary: 0, gap: 0.05 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, k.pick([0, 1, 2, 3, 4])), dur: 0.6, vol: 0.11, bright: 0.75 });
  woodblock(k, { f: 1600, vol: 0.06 });
});

registerSfx(
  'social.dance.perfect',
  { label: 'รำกลุ่ม: เพอร์เฟกต์', cat: 'social', vary: 0, gap: 0.05 },
  (k) => {
    k.pluck({ f: note(ROOT_D * 2, k.pick([2, 3, 4, 5])), dur: 0.8, vol: 0.12, bright: 0.85 });
    chime(k, { f: note(ROOT_D * 4, 4), vol: 0.035, dur: 0.6 });
  },
);

registerSfx('social.dance.miss', { label: 'รำกลุ่ม: พลาด', cat: 'social', vary: 0.04, gap: 0.05 }, (k) => {
  k.thump({ f0: 140, f1: 90, dur: 0.12, vol: 0.18 });
  k.pluck({ f: note(ROOT_D, 1) * 1.06, dur: 0.4, vol: 0.07, bright: 0.2 });
});

registerSfx(
  'social.dance.round',
  { label: 'รำกลุ่ม: จบรอบ', cat: 'social', vary: 0, gap: 0.5, preview: 1.6 },
  (k) => {
    // A small cymbal crash.
    k.noise({ dur: 1.2, vol: 0.16, attack: 0.003, type: 'highpass', f: 4500 });
    k.metal({
      f: 523,
      dur: 1,
      vol: 0.04,
      partials: [
        [1, 1, 1],
        [1.83, 0.7, 0.8],
        [2.97, 0.5, 0.6],
        [4.42, 0.4, 0.5],
      ],
    });
  },
);

registerSfx(
  'social.dance.end',
  { label: 'รำกลุ่ม: จบการรำ', cat: 'social', vary: 0, gap: 1, preview: 2.6 },
  (k) => {
    strum(k, ROOT_D, [0, 2, 4, 5, 7], { gap: 0.06, vol: 0.11 });
    gong(k, { f: 146.8, at: 0.3, dur: 2, vol: 0.08 });
  },
);

registerSfx('social.join', { label: 'เข้าร่วม', cat: 'social', vary: 0.02, gap: 0.2 }, (k) => {
  k.pluck({ f: note(ROOT_D * 2, 2), dur: 0.5, vol: 0.08 });
  k.pluck({ f: note(ROOT_D * 2, 4), at: 0.06, dur: 0.6, vol: 0.08 });
});
